import { Fragment, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

const HEADER_TO_KEYS = {
  Firstname: "firstname",
  Lastname: "lastname",
  Email: "email",
  "Ticket Name": "ticketName",
  Options: "optionsText",
  Quantity: "quantity",
};

const REQUIRED_HEADERS = ["Firstname", "Lastname", "Email", "Ticket Name"];

const createRowId = () => `participant-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const createEmptyParticipant = () => ({
  rowId: createRowId(),
  firstname: "",
  lastname: "",
  email: "",
  ticketId: "",
  ticketName: "",
  optionsText: "",
  optionSelections: [],
  optionsParseError: false,
  quantity: "1",
  error: "",
});

const parseCsv = (text) => {
  const normalized = String(text || "").replace(/^\uFEFF/, "");
  const firstLine = normalized.split(/\r?\n/, 1)[0] || "";
  const delimiter = (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ";" : ",";
  const rows = [];
  let row = [];
  let value = "";
  let inQuotes = false;

  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index];
    const nextChar = normalized[index + 1];
    if (char === '"' && inQuotes && nextChar === '"') {
      value += '"';
      index += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === delimiter && !inQuotes) {
      row.push(value);
      value = "";
    } else if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") index += 1;
      row.push(value);
      rows.push(row);
      row = [];
      value = "";
    } else {
      value += char;
    }
  }
  if (value !== "" || row.length > 0) {
    row.push(value);
    rows.push(row);
  }
  if (String(rows[0]?.[0] || "").trim().toLowerCase().replace(/[;,]$/, "") === "sep=") {
    rows.shift();
  }
  return rows;
};

const escapeCsvValue = (value) => {
  const text = String(value ?? "");
  return /[",\n\r;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const normalizedText = (value) => String(value || "").trim().toLocaleLowerCase();

const parseOptionsText = (rawOptions, ticket) => {
  const text = String(rawOptions || "").trim();
  if (!text) return { optionSelections: [] };

  const optionSelections = [];
  for (const assignment of text.split("|").map((value) => value.trim()).filter(Boolean)) {
    const [rawGroupName, rawValueText, ...extra] = assignment.split("=");
    if (!rawGroupName || !rawValueText || extra.length > 0) {
      return { error: "invalid" };
    }
    const group = (ticket.options || []).find(
      (item) => normalizedText(item.name) === normalizedText(rawGroupName),
    );
    if (!group) return { error: "invalid" };

    for (const rawValueName of rawValueText.split("+").map((value) => value.trim()).filter(Boolean)) {
      const value = (group.values || []).find(
        (item) => normalizedText(item.label) === normalizedText(rawValueName),
      );
      if (!value) return { error: "invalid" };
      optionSelections.push({ groupId: group.id, valueId: value.id });
    }
  }
  return { optionSelections };
};

const formatOptionsText = (ticket, optionSelections = []) =>
  (ticket?.options || [])
    .map((group) => {
      const labels = optionSelections
        .filter((selection) => selection.groupId === group.id)
        .map((selection) => (group.values || []).find((value) => value.id === selection.valueId)?.label)
        .filter(Boolean);
      return labels.length ? `${group.name}=${labels.join("+")}` : "";
    })
    .filter(Boolean)
    .join("|");

export default function CsvForm({ tickets, setParticipants, participants, participantPrice }) {
  const participantsInputRef = useRef(null);
  const [csvError, setCsvError] = useState("");
  const [openOptionsRowId, setOpenOptionsRowId] = useState(null);
  const { t } = useTranslation("eventdesc");

  useEffect(() => {
    if (participants.length === 0) setParticipants([createEmptyParticipant()]);
  }, [participants.length, setParticipants]);

  const exportParticipantsTemplate = () => {
    const userHeaders = ["Firstname", "Lastname", "Email", "Ticket Name", "Options", "Quantity"];
    const ticketNameList = tickets
      .map((ticket) => {
        const options = (ticket.options || [])
          .map((group) => `${group.name}=${(group.values || []).map((value) => value.label).join("+")}`)
          .join(" | ");
        return `# ${t("available_ticket_name")}: ${ticket.name}${options ? ` — ${options}` : ""}`;
      })
      .join("\r\n");
    const content = `\uFEFFsep=;\r\n${userHeaders.map(escapeCsvValue).join(";")}\r\n\r\n# ${t(
      "available_ticket_names",
    )}\r\n# ${t("optionsCsvFormat")}\r\n${ticketNameList}\r\n`;
    const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "tickets_template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const importParticipants = (event) => {
    const file = event.target.files[0];
    participantsInputRef.current.value = "";
    if (!file) return;
    const reader = new FileReader();
    setCsvError("");

    reader.onload = (loadEvent) => {
      try {
        let data;
        if (/\.numbers$/i.test(file.name)) {
          const workbook = XLSX.read(new Uint8Array(loadEvent.target.result), { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
          if (String(data[0]?.[0] || "").trim().toLowerCase().replace(/[;,]$/, "") === "sep=") {
            data.shift();
          }
        } else {
          data = parseCsv(loadEvent.target.result);
        }

        const colNames = data[0]?.map((column) => String(column || "").trim()) || [];
        if (!REQUIRED_HEADERS.every((key) => colNames.includes(key))) {
          setCsvError(t("file_header_warning"));
          return;
        }

        const importedParticipants = data.slice(1).reduce((rows, row) => {
          if (row.every((value) => String(value || "").trim() === "")) return rows;
          if (String(row[0] || "").trim().startsWith("#")) return rows;

          const participant = { ...createEmptyParticipant() };
          Object.entries(HEADER_TO_KEYS).forEach(([header, key]) => {
            const columnIndex = colNames.indexOf(header);
            if (columnIndex !== -1) participant[key] = String(row[columnIndex] ?? "").trim();
          });
          const matchingTickets = tickets.filter(
            (ticket) =>
              String(ticket.name || "").trim().toLowerCase() === participant.ticketName.toLowerCase(),
          );
          if (matchingTickets.length === 1) {
            const ticket = matchingTickets[0];
            participant.ticketId = ticket.id;
            const parsedOptions = parseOptionsText(participant.optionsText, ticket);
            if (parsedOptions.error) {
              participant.optionsParseError = true;
              participant.error = t("invalidOptionsFormat");
            }
            else participant.optionSelections = parsedOptions.optionSelections;
          }
          else if (participant.ticketName) {
            participant.error = t("invalid_ticket_name", { ticketName: participant.ticketName });
          }
          rows.push(participant);
          return rows;
        }, []);

        if (!importedParticipants.length) {
          setCsvError(t("file_empty_or_invalid"));
          return;
        }
        setParticipants(importedParticipants);
      } catch (error) {
        console.error("Participant import failed:", error);
        setCsvError(t("csv_import_error"));
      }
    };

    if (/\.numbers$/i.test(file.name)) reader.readAsArrayBuffer(file);
    else reader.readAsText(file, "utf-8");
  };

  const updateParticipant = (rowId, field, value) => {
    setParticipants((current) =>
      current.map((participant) => {
        if (participant.rowId !== rowId || participant.participantData) return participant;
        if (field !== "ticketId") return { ...participant, [field]: value, error: "" };
        const ticket = tickets.find((item) => item.id === value);
        return {
          ...participant,
          ticketId: value,
          ticketName: ticket?.name || "",
          optionsText: "",
          optionSelections: [],
          optionsParseError: false,
          error: "",
        };
      }),
    );
  };

  const updateOptionSelection = (participant, group, value, checked) => {
    setParticipants((current) =>
      current.map((row) => {
        if (row.rowId !== participant.rowId || row.participantData) return row;
        const previous = row.optionSelections || [];
        const withoutGroup = previous.filter((selection) => selection.groupId !== group.id);
        const groupSelections = previous.filter((selection) => selection.groupId === group.id);
        let nextGroupSelections;

        if (group.selectionType === "multi") {
          nextGroupSelections = checked
            ? [...groupSelections.filter((selection) => selection.valueId !== value.id), { groupId: group.id, valueId: value.id }]
            : groupSelections.filter((selection) => selection.valueId !== value.id);
          const maxSelect = Number(group.maxSelect || 0);
          if (maxSelect > 0 && nextGroupSelections.length > maxSelect) return row;
        } else {
          nextGroupSelections = checked ? [{ groupId: group.id, valueId: value.id }] : [];
        }

        const optionSelections = [...withoutGroup, ...nextGroupSelections];
        const ticket = tickets.find((item) => item.id === row.ticketId);
        return {
          ...row,
          optionSelections,
          optionsText: formatOptionsText(ticket, optionSelections),
          optionsParseError: false,
          error: "",
        };
      }),
    );
  };

  const removeParticipant = (rowId) => {
    setParticipants((current) => {
      const next = current.filter((participant) => participant.rowId !== rowId);
      return next.length ? next : [createEmptyParticipant()];
    });
  };

  const duplicateParticipant = (rowId) => {
    setParticipants((current) => {
      const rowIndex = current.findIndex((participant) => participant.rowId === rowId);
      const participant = current[rowIndex];
      if (!participant || participant.participantData) return current;

      const duplicate = {
        ...createEmptyParticipant(),
        firstname: participant.firstname,
        lastname: participant.lastname,
        email: participant.email,
        ticketId: participant.ticketId,
        ticketName: participant.ticketName,
        optionsText: participant.optionsText,
        optionSelections: participant.optionSelections,
        optionsParseError: participant.optionsParseError,
        quantity: participant.quantity,
      };
      return [...current.slice(0, rowIndex + 1), duplicate, ...current.slice(rowIndex + 1)];
    });
  };

  return (
    <div className="__excel-ticketing partner-bulk-form">
      <div className="__excel-ticketing-header">
        <div className="partner-bulk-form-title">
          <h4>{t("selected_participants")}</h4>
          <span>{participants.length}</span>
        </div>
        <div className="__excel-ticketing-header-buttons">
          <div className="__excel-ticketing-header-buttons-container">
            <button type="button" className="__excel-ticketing-header-buttons-container-btn partner-bulk-form-export" onClick={exportParticipantsTemplate}>{t("export_template_csv")}</button>
            <input type="file" accept=".csv,.numbers,text/csv" onChange={importParticipants} hidden ref={participantsInputRef} />
            <button type="button" className="__excel-ticketing-header-buttons-container-btn partner-bulk-form-import" onClick={() => participantsInputRef.current?.click()}>{t("import_values_csv_or_numbers")}</button>
          </div>
        </div>
      </div>

      <div className="__excel-ticketing-table">
        <div className="partner-participants-table" role="region" aria-label={t("selected_participants")}>
          <table>
            <thead>
              <tr>
                <th>{t("firstname")}</th>
                <th>{t("lastname")}</th>
                <th>{t("email")}</th>
                <th>{t("ticketName")}</th>
                <th>{t("ticketOptions")}</th>
                <th>{t("quantity")}</th>
                {participantPrice && <th>{t("total_amount")}</th>}
                <th className="partner-participants-table-error-heading">{t("import_error")}</th>
                <th className="partner-participants-table-actions-heading">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((participant) => {
                const isDeliveryRetry = Boolean(participant.participantData);
                const ticket = tickets.find((item) => item.id === participant.ticketId);
                return (
                  <Fragment key={participant.rowId}>
                    <tr className={participant.error ? "has-error" : ""}>
                      <td><input value={participant.firstname} disabled={isDeliveryRetry} onChange={(event) => updateParticipant(participant.rowId, "firstname", event.target.value)} aria-label={t("firstname")} /></td>
                      <td><input value={participant.lastname} disabled={isDeliveryRetry} onChange={(event) => updateParticipant(participant.rowId, "lastname", event.target.value)} aria-label={t("lastname")} /></td>
                      <td><input type="email" value={participant.email} disabled={isDeliveryRetry} onChange={(event) => updateParticipant(participant.rowId, "email", event.target.value)} aria-label={t("email")} /></td>
                      <td><select value={participant.ticketId} disabled={isDeliveryRetry} onChange={(event) => updateParticipant(participant.rowId, "ticketId", event.target.value)} aria-label={t("ticketName")}><option value="">{t("select_ticket")}</option>{tickets.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></td>
                      <td>
                        {ticket?.options?.length ? (
                          <button type="button" className="partner-participants-table-options" onClick={() => setOpenOptionsRowId((current) => current === participant.rowId ? null : participant.rowId)} disabled={isDeliveryRetry}>
                            {participant.optionsText || t("configureOptions")}
                          </button>
                        ) : "—"}
                      </td>
                      <td><input type="number" min="1" step="1" value={participant.quantity} disabled={isDeliveryRetry} onChange={(event) => updateParticipant(participant.rowId, "quantity", event.target.value)} aria-label={t("quantity")} /></td>
                      {participantPrice && <td>{participantPrice(participant).toFixed(2)} €</td>}
                      <td className="partner-participants-table-error" aria-live="polite">{isDeliveryRetry ? t("delivery_retry") : participant.error || "—"}</td>
                      <td className="partner-participants-table-actions">
                        <button type="button" className="partner-participants-table-duplicate" onClick={() => duplicateParticipant(participant.rowId)} disabled={isDeliveryRetry}>{t("duplicate")}</button>
                        <button type="button" className="partner-participants-table-remove" onClick={() => removeParticipant(participant.rowId)} aria-label={t("remove")}>×</button>
                      </td>
                    </tr>
                    {ticket?.options?.length && openOptionsRowId === participant.rowId && (
                      <tr key={`${participant.rowId}-options`} className="partner-participants-table-options-row">
                        <td colSpan={participantPrice ? 9 : 8}>
                          {(ticket.options || []).map((group) => (
                            <fieldset key={group.id} className="partner-participants-table-options-group">
                              <legend>{group.name}{group.required ? " *" : ""}</legend>
                              {(group.values || []).map((value) => {
                                const checked = (participant.optionSelections || []).some((selection) => selection.groupId === group.id && selection.valueId === value.id);
                                return <label key={value.id}><input type={group.selectionType === "multi" ? "checkbox" : "radio"} name={`${participant.rowId}-${group.id}`} checked={checked} onChange={(event) => updateOptionSelection(participant, group, value, event.target.checked)} />{value.label}</label>;
                              })}
                            </fieldset>
                          ))}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <button type="button" className="partner-participants-table-add" onClick={() => setParticipants((current) => [...current, createEmptyParticipant()])}>{t("add_participant_row")}</button>
      </div>
      <p className="__excel-ticketing-error">{csvError}</p>
    </div>
  );
}
